<template>
  <section class="column-panel">
    <article class="screen-card chart-card chart-card--inspection">
      <div class="top-tools inspection-top-tools">
        <div class="segmented inspection-segmented">
          <button
            v-for="(item, i) in periodTabs"
            :key="item"
            type="button"
            :class="{ active: i === periodIndex }"
            @click="handlePeriodChange(i)"
          >
            {{ item }}
          </button>
        </div>
      </div>
      <TabHeader :items="['查检作业情况']" :activeIndex="0">
        <div class="tab-switch-group">
          <span
            v-for="(btn, i) in inspectionBtns"
            :key="btn"
            class="tab-btn inspection-tab-btn"
            :class="{ active: i === inspectionBtnIdx }"
            @click="inspectionBtnIdx = i"
          >{{ btn }}</span>
        </div>
      </TabHeader>
      <div class="inspection-chart-shell">
        <div class="inspection-chart-head">
          <span class="inspection-axis-unit">{{ inspectionAxisUnit }}</span>
          <div class="inspection-summary">
            <div
              v-for="item in inspectionMetrics"
              :key="item.label"
              class="inspection-metric"
            >
              <span class="legend-item inspection-legend-item inspection-metric-label">
                <i class="legend-swatch inspection-legend-swatch" :style="{ background: item.color }"></i>
                <span>{{ item.label }}</span>
              </span>
              <span class="inspection-value" :class="item.valueClass">{{ item.value }}</span>
            </div>
          </div>
        </div>
        <div ref="inspectionChart" class="chart-box short inspection-chart-box"></div>
      </div>
    </article>

    <article class="screen-card chart-card filing-card">
      <TabHeader :items="['备案数量']" :activeIndex="0"></TabHeader>
      <div class="filing-body">
        <div class="chart-unit">个</div>
        <div class="filing-legend">
          <span
            v-for="item in filingLegendItems"
            :key="item.name"
            class="filing-legend-item"
          >
            <i class="filing-legend-dot" :style="{ background: item.color, color: item.color }"></i>
            <span>{{ item.label }}</span>
          </span>
        </div>
        <div ref="filingChart" class="chart-box short filing-chart-box"></div>
      </div>
    </article>

    <article class="screen-card chart-card tianshan-card">
      <TabHeader :items="['天山号']" :activeIndex="0"></TabHeader>
      <div ref="tianshanChart" class="chart-box short tianshan-chart-box"></div>
    </article>

    <article class="screen-card info-card people-card">
      <TabHeader :items="['统筹执法情况']" :activeIndex="0">
        <div class="tab-switch-group people-tab-group">
          <span
            v-for="(btn, i) in peopleBtns"
            :key="btn"
            class="tab-btn people-tab-btn"
            :class="{ active: i === peopleBtnIdx }"
            @click="peopleBtnIdx = i"
          >{{ btn }}</span>
        </div>
      </TabHeader>
      <div
        class="person-table"
        :class="`person-table--${currentPeopleView.key}`"
        :style="personTableStyle"
      >
        <div class="person-header">
          <span
            v-for="column in currentPeopleView.columns"
            :key="column.key"
            class="person-header-cell"
            :class="`person-header-cell--${column.align || 'center'}`"
          >
            {{ column.label }}
          </span>
        </div>
        <div class="person-body">
          <div
            v-for="item in filteredPeopleRows"
            :key="item.id"
            class="person-row"
          >
            <span
              v-for="column in currentPeopleView.columns"
              :key="column.key"
              class="person-cell"
              :class="[
                `person-cell--${column.align || 'center'}`,
                column.emphasis ? `person-cell--${column.emphasis}` : '',
                { 'person-cell--tooltip': Boolean(getPersonCellTooltip(item, column)) }
              ]"
              :data-tooltip="getPersonCellTooltip(item, column)"
              @mouseenter="handlePersonCellHover(item, column, true)"
              @mouseleave="handlePersonCellHover(item, column, false)"
            >
              {{ getPersonCellValue(item, column) }}
            </span>
          </div>
          <div v-if="!filteredPeopleRows.length" class="person-empty">
            暂无数据
          </div>
        </div>
      </div>
    </article>
  </section>
</template>

<script>
import * as echarts from 'echarts'
import TabHeader from '@/components/common/TabHeader.vue'
import {
  getCenterFilingChart,
  getLeftRailwayChart,
  getRightDutyRows,
  getRightLawRows,
  getRightMiddleChart,
  getRightQuarantineRows,
  getRightTerritoryChart,
  getRightTransferSummary,
  getRightUseSummary
} from '@/api/datav'

const PEOPLE_REGION_BLACKLIST = ['乌拉斯台', '乌剌斯太']

export default {
  name: 'RightPanel',
  props: {
    periodKey: {
      type: String,
      default: 'day'
    },
    dateRange: {
      type: Object,
      default: () => ({})
    }
  },
  components: { TabHeader },
  data() {
    return {
      chartInstances: [],
      periodTabs: ['日数据', '月数据', '年数据'],
      inspectionBtns: ['事中查验', '属地查检'],
      inspectionBtnIdx: 0,
      peopleBtns: ['外勤执法', /* '在岗情况', */ '检验检疫签证'],
      peopleViewKeys: ['field', /* 'onduty', */ 'visa'],
      peopleBtnIdx: 0,
      hoveredPersonCellId: null,
      inspectionHoverIndex: null,
      grainBtns: ['调运联系单', '下脚料', '粮食加工'],
      grainBtnIdx: 2,
      remoteInspectionData: {
        middle: null,
        territory: null
      },
      remoteFilingTrend: null,
      remoteTianshanData: null,
      remotePeopleRows: {
        field: [],
        onduty: [],
        visa: []
      },
      remoteTransferSummary: null,
      remoteUseSummary: null,
      inspectionStats: { pending: '', today: '', rate: '' },
      inspectionTrend: {
        labels: [],
        pending: [],
        today: [],
        rate: []
      },
      filingTrend: {
        labels: [],
        series: []
      },
      tianshanData: {
        labels: [],
        legend: ['天山号列数', '天山号标箱'],
        series: [
          { name: '天山号列数', data: [] },
          { name: '天山号标箱', data: [] }
        ]
      },
      inspectionLegend: [
        { key: 'pending', color: '#19dcff' },
        { key: 'today', color: '#ffd247' },
        { key: 'rate', color: '#82f34d' }
      ],
      peopleViews: [
        {
          key: 'field',
          gridTemplate: 'minmax(74px, 0.9fr) minmax(78px, 0.9fr) minmax(0, 1.5fr) minmax(98px, 0.96fr)',
          columns: [
            { key: 'officerCode', label: '工号', align: 'center', tooltipKey: 'officerName' },
            { key: 'location', label: '地点', align: 'left', tooltip: false },
            { key: 'taskContent', label: '作业内容', align: 'left', tooltip: false },
            { key: 'date', label: '日期', align: 'center', tooltip: false }
          ],
          rows: []
        },
        {
          key: 'onduty',
          gridTemplate: 'minmax(0, 1.1fr) minmax(130px, 1.2fr) minmax(105px, 0.96fr) minmax(100px, 0.86fr)',
          columns: [
            { key: 'dept', label: '科室', align: 'left', tooltip: false },
            { key: 'formalPct', label: '科室、关员在岗率', align: 'center', emphasis: 'primary', tooltip: false },
            { key: 'auxiliaryPct', label: '协勤在岗率', align: 'center', emphasis: 'primary', tooltip: false },
            { key: 'date', label: '日期', align: 'center', tooltip: false }
          ],
          rows: []
        },
        {
          key: 'visa',
          gridTemplate: 'minmax(88px, 0.86fr) minmax(0, 1.54fr)',
          columns: [
            { key: 'officerCode', label: '数量', align: 'center', tooltip: false },
            { key: 'type', label: '签证类型', align: 'left', tooltip: false }
          ],
          rows: []
        }
      ],
      grainCards: {
        transfer: {
          title: '调运联系单',
          showGauge: false,
          gaugeValue: null,
          gaugeLabel: '',
          stats: []
        },
        residue: {
          title: '下脚料',
          showGauge: false,
          gaugeValue: null,
          gaugeLabel: '',
          stats: []
        },
        processing: {
          title: '粮食加工',
          showGauge: false,
          gaugeValue: null,
          gaugeLabel: '',
          stats: []
        }
      }
    }
  },
  computed: {
    periodIndex() {
      return ['day', 'month', 'year'].indexOf(this.periodKey)
    },
    currentPeriodKey() {
      return this.periodKey || 'day'
    },
    currentInspectionTypeKey() {
      return this.inspectionBtnIdx === 0 ? 'middle' : 'territory'
    },
    resolvedInspectionData() {
      const remoteInspection = this.remoteInspectionData[this.currentInspectionTypeKey]

      if (remoteInspection) {
        return remoteInspection
      }

      return {
        labels: this.inspectionTrend.labels,
        pending: this.inspectionTrend.pending,
        today: this.inspectionTrend.today,
        rate: this.inspectionTrend.rate,
        summary: {
          pending: this.inspectionStats.pending,
          today: this.inspectionStats.today,
          rate: this.inspectionStats.rate
        }
      }
    },
    resolvedFilingTrend() {
      if (this.remoteFilingTrend && Array.isArray(this.remoteFilingTrend.series)) {
        return this.remoteFilingTrend
      }
      return this.filingTrend
    },
    resolvedTianshanData() {
      const source = this.remoteTianshanData && Array.isArray(this.remoteTianshanData.series)
        ? this.remoteTianshanData
        : this.tianshanData
      const names = ['天山号列数', '天山号标箱']
      return {
        labels: Array.isArray(source.labels) ? source.labels : [],
        legend: names,
        series: names.map((name, index) => {
          const item = (source.series || []).find(seriesItem => seriesItem && seriesItem.name === name)
          return item || this.tianshanData.series[index]
        })
      }
    },
    filingLegendItems() {
      const series = Array.isArray(this.resolvedFilingTrend.series) ? this.resolvedFilingTrend.series : []
      return series.map(item => ({
        name: item.name,
        label: item.label,
        color: Array.isArray(item.colors) ? item.colors[0] : '#55d2ff'
      }))
    },
    inspectionMetrics() {
      return [
        {
          ...this.inspectionLegend[0],
          label: '待查验量',
          value: this.resolvedInspectionSummary.pending,
          valueClass: 'inspection-value--pending'
        },
        {
          ...this.inspectionLegend[1],
          label: this.inspectionProcessingLabel,
          value: this.resolvedInspectionSummary.today,
          valueClass: 'inspection-value--today'
        },
        {
          ...this.inspectionLegend[2],
          label: this.inspectionExceptionLabel,
          value: this.resolvedInspectionSummary.rate,
          valueClass: 'inspection-value--rate'
        }
      ]
    },
    inspectionProcessingLabel() {
      return '已查验量'
    },
    inspectionExceptionLabel() {
      return this.currentInspectionTypeKey === 'territory' ? '发现异常（批）' : '查获率'
    },
    inspectionAxisUnit() {
      return this.currentInspectionTypeKey === 'territory' ? '个/批' : '个/%'
    },
    resolvedInspectionSummary() {
      const summary = this.resolvedInspectionData.summary || {
        pending: this.inspectionStats.pending,
        today: this.inspectionStats.today,
        rate: this.inspectionStats.rate
      }
      const hoverIndex = this.inspectionHoverIndex
      const pendingSeries = Array.isArray(this.resolvedInspectionData.pending) ? this.resolvedInspectionData.pending : []
      const todaySeries = Array.isArray(this.resolvedInspectionData.today) ? this.resolvedInspectionData.today : []
      const rateSeries = Array.isArray(this.resolvedInspectionData.rate) ? this.resolvedInspectionData.rate : []

      if (
        Number.isInteger(hoverIndex) &&
        hoverIndex >= 0 &&
        hoverIndex < pendingSeries.length &&
        hoverIndex < todaySeries.length &&
        hoverIndex < rateSeries.length
      ) {
        return {
          pending: this.formatInspectionCountValue(pendingSeries[hoverIndex]),
          today: this.formatInspectionCountValue(todaySeries[hoverIndex]),
          rate: this.formatInspectionThirdValue(rateSeries[hoverIndex])
        }
      }

      return {
        pending: this.formatInspectionCountValue(summary.pending),
        today: this.formatInspectionCountValue(summary.today),
        rate: this.formatInspectionThirdValue(summary.rate)
      }
    },
    currentPeopleView() {
      const activeViewKey = this.peopleViewKeys[this.peopleBtnIdx] || this.peopleViewKeys[0]
      const baseView = this.peopleViews.find(item => item.key === activeViewKey) || this.peopleViews[0]
      const remoteRows = Array.isArray(this.remotePeopleRows[baseView.key]) ? this.remotePeopleRows[baseView.key] : null
      const rows = (remoteRows || baseView.rows).filter(item => !this.isHiddenPeopleRegion(item.region))

      return {
        ...baseView,
        rows
      }
    },
    filteredPeopleRows() {
      return this.currentPeopleView.rows || []
    },
    personTableStyle() {
      return {
        '--person-grid-template': this.currentPeopleView.gridTemplate
      }
    },
    currentGrainTabKey() {
      return ['transfer', 'residue', 'processing'][this.grainBtnIdx] || 'processing'
    },
    resolvedGrainCard() {
      const fallbackCard = this.grainCards[this.currentGrainTabKey] || this.grainCards.processing
      const transferSummary = this.remoteTransferSummary || {}
      const useSummary = this.remoteUseSummary || {}
      const processingTotal = Number(useSummary.processingTotal || 0)
      const transferTotal = Number(transferSummary.transferTotal || 0)
      const offcutsTotal = Number(useSummary.offcutsTotal || 0)
      const offcutsHandledTotal = Number(useSummary.offcutsHandledTotal || 0)
      const hasTransferData = Boolean(transferSummary && (transferSummary.transferCount || transferSummary.transferTotal))
      const hasUseData = Boolean(useSummary && (useSummary.processingTotal || useSummary.offcutsTotal || useSummary.offcutsHandledTotal))

      if (this.currentGrainTabKey === 'transfer') {
        return {
          ...fallbackCard,
          title: '调运联系单',
          showGauge: false,
          gaugeValue: null,
          gaugeLabel: '',
          stats: hasTransferData || hasUseData
            ? [
                { value: transferSummary.transferCount || '', unit: '次', label: '调运数量', tone: 'primary' },
                { value: useSummary.processingTotal || '', unit: '吨', label: '已加工数量', tone: 'secondary' }
              ]
            : []
        }
      }

      if (this.currentGrainTabKey === 'residue') {
        return {
          ...fallbackCard,
          title: '下脚料',
          showGauge: hasUseData,
          gaugeValue: offcutsTotal > 0 ? Math.round((offcutsHandledTotal / offcutsTotal) * 1000) / 10 : 0,
          gaugeLabel: hasUseData ? '处理率' : '',
          stats: hasUseData
            ? [
                { value: useSummary.offcutsTotal || '', unit: 'kg', label: '下脚料重量', tone: 'primary' },
                { value: useSummary.offcutsHandledTotal || '', unit: 'kg', label: '已处理重量', tone: 'secondary' }
              ]
            : []
        }
      }

      return {
        ...fallbackCard,
        title: '粮食加工',
        showGauge: hasTransferData || hasUseData,
        gaugeValue: transferTotal > 0 ? Math.round((processingTotal / transferTotal) * 1000) / 10 : 0,
        gaugeLabel: hasTransferData || hasUseData ? '加工率' : '',
        stats: hasTransferData || hasUseData
          ? [
              { value: transferSummary.transferTotal || '', unit: '吨', label: '调入总量', tone: 'primary' },
              { value: useSummary.processingTotal || '', unit: '吨', label: '已加工数量', tone: 'secondary' }
            ]
          : []
      }
    },
    displayGrainStats() {
      return Array.isArray(this.resolvedGrainCard.stats) ? this.resolvedGrainCard.stats : []
    },
    showGrainGauge() {
      return Boolean(this.resolvedGrainCard.showGauge)
    },
    showGrainHeading() {
      return this.currentGrainTabKey === 'transfer'
    },
    currentGrainGaugeValue() {
      const value = Number(this.resolvedGrainCard.gaugeValue)
      return Number.isFinite(value) ? value : 0
    }
  },
  watch: {
    grainBtnIdx() {
      this.$nextTick(() => {
        this.drawGrainGauge()
      })
    },
    inspectionBtnIdx() {
      this.clearInspectionHoverIndex()
      this.updateInspectionChart()
    },
    async periodKey() {
      this.clearInspectionHoverIndex()
      await Promise.all([this.fetchInspectionData(), this.fetchPanelData()])
      this.updateInspectionChart()
      this.updateFilingChart()
      this.$nextTick(() => {
        this.drawGrainGauge()
      })
    },
    dateRange: {
      deep: true,
      async handler() {
        this.clearInspectionHoverIndex()
        await Promise.all([this.fetchInspectionData(), this.fetchPanelData()])
        this.updateInspectionChart()
        this.updateFilingChart()
        this.$nextTick(() => {
          this.drawGrainGauge()
        })
      }
    }
  },
  async mounted() {
    this.initCharts()
    await Promise.all([this.fetchInspectionData(), this.fetchPanelData()])
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.chartInstances.forEach(chart => chart.dispose())
  },
  methods: {
    handlePeriodChange(index) {
      this.$emit('period-change', ['day', 'month', 'year'][index] || 'day')
    },
    getPersonCellValue(item, column) {
      const valueKey = column.displayKey || column.key
      const hoverValueKey = column.hoverDisplayKey || column.tooltipKey

      if (hoverValueKey && this.isPersonCellHovered(item, column)) {
        return item[hoverValueKey] || item[valueKey]
      }

      return item[valueKey]
    },
    getPersonCellTooltip(item, column) {
      if (column.tooltip === false) return null
      const tooltipKey = column.tooltipKey || column.displayKey || column.key
      return item[tooltipKey] || null
    },
    getPersonCellHoverId(item, column) {
      if (!this.getPersonCellTooltip(item, column)) return null
      return `${item.id}:${column.key}`
    },
    isPersonCellHovered(item, column) {
      return this.hoveredPersonCellId === this.getPersonCellHoverId(item, column)
    },
    handlePersonCellHover(item, column, isHovered) {
      const hoverId = this.getPersonCellHoverId(item, column)
      if (!hoverId) return
      this.hoveredPersonCellId = isHovered ? hoverId : null
    },
    isHiddenPeopleRegion(region) {
      return PEOPLE_REGION_BLACKLIST.includes(region)
    },
    initCharts() {
      const inspectionChart = this.chartInstances[0] || echarts.init(this.$refs.inspectionChart)
      const filingChart = this.chartInstances[1] || echarts.init(this.$refs.filingChart)
      const tianshanChart = this.chartInstances[2] || echarts.init(this.$refs.tianshanChart)
      this.chartInstances = [inspectionChart, filingChart, tianshanChart]
      if (!this.inspectionGlobalOutHandler) {
        this.inspectionGlobalOutHandler = () => {
          this.clearInspectionHoverIndex()
        }
      }
      const zr = inspectionChart.getZr()
      if (zr && typeof zr.on === 'function') {
        zr.on('globalout', this.inspectionGlobalOutHandler)
      }
      this.updateInspectionChart()
      this.updateFilingChart()
      this.updateTianshanChart()
    },
    async fetchInspectionData() {
      const period = this.currentPeriodKey
      const dateRange = this.dateRange || {}
      try {
        const [middle, territory] = await Promise.all([
          getRightMiddleChart(period, dateRange),
          getRightTerritoryChart(period, dateRange)
        ])
        this.remoteInspectionData = { middle, territory }
        this.$nextTick(() => {
          this.updateInspectionChart()
        })
      } catch (error) {
        console.error('右侧查验图表数据加载失败', error)
      }
    },
    async fetchPanelData() {
      const period = this.currentPeriodKey
      const dateRange = this.dateRange || {}
      try {
        const [lawRows, dutyRows, quarantineRows, transferSummary, useSummary, filingTrend, tianshanData] = await Promise.all([
          getRightLawRows(period, dateRange),
          getRightDutyRows(period, dateRange),
          getRightQuarantineRows(period, dateRange),
          getRightTransferSummary(period, dateRange),
          getRightUseSummary(period, dateRange),
          getCenterFilingChart(period, dateRange),
          getLeftRailwayChart(period, dateRange)
        ])
        this.remotePeopleRows = {
          field: Array.isArray(lawRows) ? lawRows : [],
          onduty: Array.isArray(dutyRows) ? dutyRows : [],
          visa: Array.isArray(quarantineRows) ? quarantineRows : []
        }
        this.remoteTransferSummary = transferSummary || null
        this.remoteUseSummary = useSummary || null
        this.remoteFilingTrend = filingTrend || null
        this.remoteTianshanData = tianshanData || null
        this.$nextTick(() => {
          this.updateFilingChart()
          this.updateTianshanChart()
        })
      } catch (error) {
        console.error('右侧面板数据加载失败', error)
      }
    },
    updateFilingChart() {
      const filingChart = this.chartInstances[1]
      if (!filingChart) {
        return
      }

      const series = Array.isArray(this.resolvedFilingTrend.series) ? this.resolvedFilingTrend.series : []
      const labels = Array.isArray(this.resolvedFilingTrend.labels) ? this.resolvedFilingTrend.labels : []
      const filingValues = series.reduce((result, item) => {
        return result.concat(Array.isArray(item.data) ? item.data : [])
      }, [])
      const filingMaxValue = filingValues.length ? Math.max(...filingValues.map(item => Number(item) || 0)) : 0
      const filingAxisMax = Math.max(Math.ceil(filingMaxValue / 100) * 100, 100)

      filingChart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(8, 28, 70, 0.92)',
          borderColor: 'rgba(87, 196, 255, 0.24)',
          textStyle: { color: '#e6f8ff', fontSize: 11 },
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(109, 196, 255, 0.08)'
            }
          }
        },
        grid: { left: 10, right: 8, top: 8, bottom: 8, containLabel: true },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: 'rgba(138, 211, 255, 0.18)' } },
          axisLabel: { color: '#bde8ff', fontSize: 10, margin: 8 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: filingAxisMax,
          interval: Math.max(filingAxisMax / 5, 1),
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: 'rgba(138, 211, 255, 0.1)', type: 'dashed' } },
          axisLabel: { color: '#9ed5ff', fontSize: 10 }
        },
        series: series.map(item => {
          const colors = Array.isArray(item.colors) && item.colors.length >= 2 ? item.colors : ['#72DEFE', '#1584DA']
          return {
            name: item.name,
            type: 'bar',
            barWidth: 8,
            barGap: '18%',
            itemStyle: {
              borderRadius: [3, 3, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors[0] },
                { offset: 1, color: colors[1] }
              ])
            },
            emphasis: {
              focus: 'series'
            },
            data: Array.isArray(item.data) ? item.data : []
          }
        })
      }, { notMerge: true })
    },
    updateTianshanChart() {
      const chart = this.chartInstances[2]
      if (!chart) {
        return
      }

      const currentData = this.resolvedTianshanData
      const trainSeries = currentData.series[0] || { name: '天山号列数', data: [] }
      const containerSeries = currentData.series[1] || { name: '天山号标箱', data: [] }
      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(3, 17, 45, 0.92)',
          borderColor: 'rgba(68, 180, 255, 0.25)',
          textStyle: { color: '#dff6ff', fontSize: 11 },
          axisPointer: {
            type: 'line',
            lineStyle: { color: 'rgba(210, 233, 255, 0.5)', type: 'dashed' }
          }
        },
        legend: {
          data: currentData.legend,
          formatter: name => name.replace(/^天山号/, ''),
          top: 2,
          left: 'center',
          icon: 'rect',
          itemWidth: 9,
          itemHeight: 9,
          itemGap: 10,
          textStyle: { color: '#c5e9ff', fontSize: 10 }
        },
        grid: { left: 32, right: 42, top: 28, bottom: 18 },
        xAxis: {
          type: 'category',
          data: currentData.labels,
          axisLine: { lineStyle: { color: 'rgba(122, 203, 255, 0.34)' } },
          axisTick: { show: false },
          axisLabel: { color: '#bde8ff', fontSize: 10, margin: 8 }
        },
        yAxis: [
          {
            type: 'value',
            name: '列',
            minInterval: 1,
            nameTextStyle: { color: '#72DEFE', fontSize: 10, padding: [0, 0, 2, -2] },
            splitLine: { lineStyle: { color: 'rgba(82, 165, 255, 0.14)', type: 'dashed' } },
            axisLine: { show: true, lineStyle: { color: 'rgba(114, 222, 254, 0.44)' } },
            axisLabel: { color: '#9ed5ff', fontSize: 10 }
          },
          {
            type: 'value',
            name: '个',
            minInterval: 1,
            nameTextStyle: { color: '#FF9A1A', fontSize: 10, padding: [0, 0, 2, 2] },
            splitLine: { show: false },
            axisLine: { show: true, lineStyle: { color: 'rgba(255, 154, 26, 0.62)' } },
            axisLabel: { color: '#ffd19a', fontSize: 10 }
          }
        ],
        series: [
          {
            name: trainSeries.name,
            type: 'bar',
            yAxisIndex: 0,
            barWidth: 9,
            itemStyle: {
              borderRadius: [2, 2, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#72DEFE' },
                { offset: 1, color: '#1584DA' }
              ])
            },
            data: trainSeries.data || []
          },
          {
            name: containerSeries.name,
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            lineStyle: { width: 2.2, color: '#FF9A1A' },
            itemStyle: { color: '#FF9A1A', borderColor: '#FF9A1A', borderWidth: 2 },
            data: containerSeries.data || []
          }
        ]
      }, { notMerge: true })
    },
    updateInspectionChart() {
      const inspectionChart = this.chartInstances[0]
      if (!inspectionChart) {
        return
      }

      const currentData = this.resolvedInspectionData
      const processingLabel = this.inspectionProcessingLabel
      const countValues = []
        .concat(currentData.pending || [])
        .concat(currentData.today || [])
        .map(item => Number(item) || 0)
      const countMax = countValues.length ? Math.max(...countValues, 0) : 0
      const countAxisMax = Math.max(30, Math.ceil(countMax / 5) * 5)
      const countAxisInterval = Math.max(Math.ceil(countAxisMax / 6), 1)
      const thirdAxisMax = this.currentInspectionTypeKey === 'territory' ? countAxisMax : 100
      const thirdAxisInterval = this.currentInspectionTypeKey === 'territory' ? countAxisInterval : 20

      inspectionChart.setOption({
        color: ['#19dcff', '#ffd247', '#82f34d'],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(7, 28, 68, 0.96)',
          borderColor: 'rgba(87, 196, 255, 0.28)',
          textStyle: { color: '#e6f8ff', fontSize: 11 },
          formatter: params => this.formatInspectionTooltip(params, processingLabel),
          axisPointer: {
            lineStyle: { color: 'rgba(116, 210, 255, 0.28)' }
          }
        },
        legend: { show: false },
        grid: { left: 12, right: 28, top: 14, bottom: 6, containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: currentData.labels || [],
          axisLine: { lineStyle: { color: 'rgba(111, 180, 232, 0.34)', width: 1 } },
          axisLabel: {
            color: '#bfdcf3',
            fontSize: 10,
            margin: 13,
            showMinLabel: true,
            showMaxLabel: true
          },
          axisTick: { show: false },
          splitLine: { show: false }
        },
        yAxis: [
          {
            type: 'value',
            min: 0,
            max: countAxisMax,
            interval: countAxisInterval,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: 'rgba(138, 211, 255, 0.16)', width: 1 } },
            axisLabel: { color: '#a8d2ef', fontSize: 10, margin: 10 }
          },
          {
            type: 'value',
            min: 0,
            max: thirdAxisMax,
            interval: thirdAxisInterval,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: {
              color: '#b8f58c',
              fontSize: 10,
              margin: 10,
              formatter: value => this.currentInspectionTypeKey === 'territory' ? value : `${value}%`
            }
          }
        ],
        series: [
          {
            name: '待查验量',
            type: 'bar',
            barWidth: 13,
            barGap: '35%',
            itemStyle: {
              borderRadius: [3, 3, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#5be5ff' },
                { offset: 1, color: '#1584da' }
              ])
            },
            data: currentData.pending || []
          },
          {
            name: processingLabel,
            type: 'bar',
            barWidth: 13,
            itemStyle: {
              borderRadius: [3, 3, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#ffe37a' },
                { offset: 1, color: '#d79318' }
              ])
            },
            data: currentData.today || []
          },
          {
            name: this.inspectionExceptionLabel,
            type: 'line',
            yAxisIndex: 1,
            smooth: 0.42,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: {
              width: 3,
              color: '#82f34d',
              shadowBlur: 8,
              shadowColor: 'rgba(130, 243, 77, 0.16)'
            },
            itemStyle: {
              color: '#82f34d'
            },
            data: currentData.rate || []
          }
        ]
      }, { notMerge: true })
    },
    formatInspectionTooltip(params, processingLabel) {
      const rows = Array.isArray(params) ? params : []
      if (!rows.length) {
        this.clearInspectionHoverIndex()
        return ''
      }

      const dataIndex = rows[0] && Number.isInteger(rows[0].dataIndex) ? rows[0].dataIndex : null
      this.inspectionHoverIndex = dataIndex
      const title = rows[0] && rows[0].axisValueLabel ? rows[0].axisValueLabel : ''

      const labelMap = {
        '待查验量': '待查验量',
        [processingLabel]: processingLabel,
        [this.inspectionExceptionLabel]: this.inspectionExceptionLabel
      }

      const lines = rows.map(item => {
        const label = labelMap[item.seriesName] || item.seriesName
        const markerColor = item.seriesName === '待查验量'
          ? '#19dcff'
          : item.seriesName === processingLabel
            ? '#ffd247'
            : '#82f34d'
        const marker = `<span style="display:inline-block;width:10px;height:10px;margin-right:8px;border-radius:50%;background:${markerColor};"></span>`
        const value = item.seriesName === this.inspectionExceptionLabel
          ? this.formatInspectionThirdValue(item.data)
          : this.formatInspectionCountValue(item.data)
        return `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin-top:6px;"><span style="display:inline-flex;align-items:center;">${marker}${label}</span><strong style="color:#ffffff;font-weight:700;">${value}</strong></div>`
      })

      return `<div><div style="margin-bottom:2px;font-weight:700;color:#dff4ff;">${title}</div>${lines.join('')}</div>`
    },
    clearInspectionHoverIndex() {
      this.inspectionHoverIndex = null
    },
    formatInspectionCountValue(value) {
      const num = Number(value)
      return Number.isFinite(num) ? `${num}` : '0'
    },
    formatInspectionThirdValue(value) {
      if (this.currentInspectionTypeKey === 'territory') {
        return this.formatInspectionCountValue(value)
      }
      return this.formatInspectionRateValue(value)
    },
    formatInspectionRateValue(value) {
      if (typeof value === 'string' && value.includes('%')) {
        return value
      }
      const num = Number(value)
      return Number.isFinite(num) ? `${num}%` : '0%'
    },
    drawGrainGauge() {
      const canvas = this.$refs.grainGaugeCanvas
      const wrap = this.$refs.grainGaugeWrap
      if (!canvas || !wrap) return

      const rect = wrap.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, width, height)

      const value = Math.max(0, Math.min(100, this.currentGrainGaugeValue))
      const cx = width * 0.455
      const cy = height * 0.925
      const radius = Math.min(width * 0.278, height * 0.68)
      const ringWidth = Math.max(radius * 0.24, 18)
      const startDeg = 180
      const endDeg = 360
      const toRad = deg => (deg * Math.PI) / 180
      const arcPoint = (deg, r) => ({
        x: cx + Math.cos(toRad(deg)) * r,
        y: cy + Math.sin(toRad(deg)) * r
      })
      const drawArc = (fromDeg, toDeg, color, lineWidth = ringWidth, lineCap = 'butt') => {
        ctx.save()
        ctx.beginPath()
        ctx.lineWidth = lineWidth
        ctx.lineCap = lineCap
        ctx.strokeStyle = color
        ctx.arc(cx, cy, radius, toRad(fromDeg), toRad(toDeg), false)
        ctx.stroke()
        ctx.restore()
      }

      drawArc(startDeg, endDeg, 'rgba(69, 112, 168, 0.16)', ringWidth + 2, 'butt')
      drawArc(180, 225, '#ffb06a', ringWidth, 'butt')
      drawArc(225, 270, '#f9f668', ringWidth, 'butt')
      drawArc(270, 315, '#8aec64', ringWidth, 'butt')
      drawArc(315, 360, '#72d975', ringWidth, 'butt')

      ctx.save()
      ctx.fillStyle = '#dff6ff'
      ctx.font = `700 ${Math.max(radius * 0.148, 11)}px "Microsoft YaHei", sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ;[
        { text: '0', x: cx - radius - ringWidth * 0.56, y: cy - ringWidth * 0.03, rotate: 0 },
        { text: '25%', deg: 225, distance: radius + ringWidth * 0.84, rotate: -38 },
        { text: '50%', x: cx, y: cy - radius - ringWidth * 0.94, rotate: 0 },
        { text: '75%', deg: 315, distance: radius + ringWidth * 0.84, rotate: 38 },
        { text: '100%', x: cx + radius + ringWidth * 0.74, y: cy - ringWidth * 0.36, rotate: 90 }
      ].forEach(item => {
        const point = item.x !== undefined
          ? { x: item.x, y: item.y }
          : arcPoint(item.deg, item.distance)
        ctx.save()
        ctx.translate(point.x, point.y)
        if (item.rotate) {
          ctx.rotate(toRad(item.rotate))
        }
        ctx.strokeText(item.text, 0, 0)
        ctx.fillText(item.text, 0, 0)
        ctx.restore()
      })
      ctx.restore()

      const topMarkOuter = arcPoint(270, radius + ringWidth * 0.08)
      const topMarkInner = arcPoint(270, radius - ringWidth * 0.42)
      ctx.save()
      ctx.beginPath()
      ctx.lineWidth = 2
      ctx.strokeStyle = 'rgba(248, 172, 54, 0.55)'
      ctx.moveTo(topMarkInner.x, topMarkInner.y)
      ctx.lineTo(topMarkOuter.x, topMarkOuter.y)
      ctx.stroke()
      ctx.restore()

      const pointerDeg = startDeg + (value / 100) * (endDeg - startDeg)
      const tip = arcPoint(pointerDeg, radius - ringWidth * 0.08)
      ctx.save()
      ctx.beginPath()
      ctx.lineWidth = Math.max(ringWidth * 0.14, 5)
      ctx.lineCap = 'round'
      ctx.strokeStyle = '#ff9a17'
      ctx.moveTo(cx, cy)
      ctx.lineTo(tip.x, tip.y)
      ctx.stroke()
      ctx.restore()

      ctx.beginPath()
      ctx.fillStyle = 'rgba(8, 28, 70, 0.92)'
      ctx.arc(cx, cy, Math.max(ringWidth * 0.18, 8), 0, Math.PI * 2)
      ctx.fill()

      ctx.beginPath()
      ctx.fillStyle = '#ff9a17'
      ctx.arc(cx, cy, Math.max(ringWidth * 0.1, 5), 0, Math.PI * 2)
      ctx.fill()
    },
    handleResize() {
      this.chartInstances.forEach(chart => chart.resize())
      this.drawGrainGauge()
    }
  }
}
</script>

<style scoped>
.column-panel {
  display: flex;
  flex-direction: column;
  gap: 0.34vh;
  padding-top: 0.08vh;
  min-height: 0;
}

.column-panel > .screen-card:nth-child(1) {
  flex: 1.58 1 0;
}

.column-panel > .screen-card:nth-child(2) {
  flex: 0.9 1 0;
}

.column-panel > .screen-card:nth-child(3) {
  flex: 0.96 1 0;
}

.column-panel > .screen-card:nth-child(4) {
  flex: 1.04 1 0;
}

.screen-card,
.info-card,
.chart-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0.46vh 0.52vw 0.38vh;
  overflow: hidden;
}

.top-tools {
  display: flex;
  justify-content: center;
  margin-bottom: 0.42vh;
  padding-top: 0.04vh;
}

.segmented {
  display: inline-grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.18vw;
  width: 100%;
  max-width: 14.9vw;
  padding: 0.14vh 0.18vw;
  border: 0.05vw solid rgba(61, 166, 255, 0.28);
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(8, 53, 120, 0.68), rgba(4, 28, 67, 0.36));
  box-shadow: inset 0 0 0.84vw rgba(37, 146, 255, 0.14), 0 0 0.5vw rgba(15, 94, 196, 0.08);
}

.segmented button {
  height: 2.18vh;
  font-size: 0.62vw;
  letter-spacing: 0.03vw;
  border: none;
  border-radius: 999vw;
  color: #a9e1ff;
  background: transparent;
  cursor: pointer;
}

.segmented button.active {
  color: #ffffff;
  background: linear-gradient(180deg, rgba(34, 188, 255, 0.96), rgba(18, 107, 235, 0.86));
  box-shadow: 0 0 0.92vw rgba(32, 180, 255, 0.24);
}

.chart-card--inspection {
  padding-top: 0.32vh;
}

.inspection-top-tools {
  justify-content: flex-start;
  margin-bottom: 0.82vh;
  padding: 0 0.18vw;
}

.inspection-segmented {
  position: relative;
  max-width: none;
  width: 100%;
  padding: 0.18vh 0.22vw;
  border: 0.05vw solid rgba(55, 189, 255, 0.32);
  border-radius: 0.9vw;
  background:
    linear-gradient(180deg, rgba(7, 55, 118, 0.92), rgba(4, 26, 67, 0.72)),
    radial-gradient(circle at top, rgba(38, 161, 255, 0.2), transparent 56%);
  box-shadow:
    inset 0 0 1.2vw rgba(31, 191, 255, 0.12),
    0 0 1vw rgba(15, 109, 211, 0.1);
}

.inspection-segmented::before,
.inspection-segmented::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 0.18vw;
  height: 1.15vh;
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(45, 228, 255, 0.98), rgba(17, 121, 255, 0.2));
  box-shadow: 0 0 0.7vw rgba(32, 198, 255, 0.44);
  transform: translateY(-50%);
}

.inspection-segmented::before {
  left: 0.18vw;
}

.inspection-segmented::after {
  right: 0.18vw;
}

.inspection-segmented button {
  position: relative;
  height: 2.86vh;
  font-size: 0.92vw;
  font-weight: 700;
  letter-spacing: 0.02vw;
  color: rgba(233, 247, 255, 0.88);
  text-shadow: 0 0 0.6vw rgba(255, 255, 255, 0.26);
}

.inspection-segmented button.active {
  color: #f6feff;
  background:
    linear-gradient(180deg, rgba(22, 208, 255, 0.4), rgba(16, 110, 246, 0.18)),
    linear-gradient(180deg, rgba(15, 94, 222, 0.98), rgba(12, 72, 175, 0.94));
  box-shadow:
    inset 0 0 0 0.06vw rgba(119, 241, 255, 0.54),
    0 0 1.1vw rgba(32, 180, 255, 0.3);
}

.inspection-segmented button.active::after {
  content: '';
  position: absolute;
  left: 18%;
  right: 18%;
  bottom: 0.28vh;
  height: 0.16vh;
  border-radius: 999vw;
  background: rgba(126, 243, 255, 0.94);
  box-shadow: 0 0 0.65vw rgba(126, 243, 255, 0.78);
}

.chart-card--inspection ::v-deep(.tab-header) {
  align-items: center;
  height: 3.5vh;
  gap: 0.42vw;
  padding-top: 0;
  padding-left: 3.18vw;
  padding-right: 0.26vw;
  padding-bottom: 0;
  margin-bottom: 0.22vh;
}

.chart-card--inspection ::v-deep(.tab-text) {
  height: 100%;
  color: #eefaff;
  font-size: 0.84vw;
  font-weight: 700;
  letter-spacing: 0;
  text-shadow: 0 0 0.76vw rgba(255, 255, 255, 0.34);
}

.chart-card--inspection .inspection-tab-btn {
  min-width: 4.86vw;
  height: 2.22vh;
  padding: 0 0.58vw;
  font-size: 0.56vw;
}

.inspection-chart-shell {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.inspection-chart-head {
  display: flex;
  align-items: flex-start;
  gap: 0.32vw;
  padding: 0.42vh 0.08vw 0;
}

.inspection-axis-unit {
  display: inline-flex;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 2.2vw;
  padding-top: 1.52vh;
  color: #cbeeff;
  font-size: 0.82vw;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  text-shadow: 0 0 0.72vw rgba(169, 223, 255, 0.36);
}

.inspection-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  flex: 1;
  min-width: 0;
  gap: 0.32vw;
  padding-top: 0.18vh;
}

.chart-card--inspection .inspection-legend-item {
  justify-content: center;
  gap: 0.3vw;
  color: #dff4ff;
  font-size: 0.56vw;
  font-weight: 600;
  white-space: nowrap;
}

.chart-card--inspection .inspection-legend-swatch {
  width: 1.12vw;
  height: 0.13vh;
  border-radius: 999vw;
  box-shadow: 0 0 0.58vw currentColor;
}

.inspection-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.66vh;
  min-width: 0;
}

.inspection-metric-label {
  width: 100%;
}

.inspection-value {
  font-size: 0.98vw;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-shadow: 0 0 0.58vw rgba(255, 255, 255, 0.14);
}

.inspection-value--pending {
  color: #18d7ff;
}

.inspection-value--today {
  color: #d5dbe8;
}

.inspection-value--rate {
  color: #91ef47;
}

.chart-card--inspection .chart-box.short.inspection-chart-box {
  flex: 1;
  min-height: 0;
  margin-top: 0.34vh;
}

.tianshan-card {
  padding-top: 0.36vh;
}

.tianshan-card ::v-deep(.tab-header) {
  height: 3.24vh;
  padding-left: 3.14vw;
  margin-bottom: 0.14vh;
}

.tianshan-card ::v-deep(.tab-text) {
  color: #eefaff;
  font-size: 0.82vw;
  font-weight: 700;
  letter-spacing: 0;
}

.tianshan-chart-box {
  flex: 1;
  min-height: 0;
  margin-top: 0.06vh;
}

.filing-card {
  padding-top: 0.36vh;
}

.filing-card ::v-deep(.tab-header) {
  height: 3.24vh;
  padding-left: 3.14vw;
  margin-bottom: 0.14vh;
}

.filing-card ::v-deep(.tab-text) {
  color: #eefaff;
  font-size: 0.82vw;
  font-weight: 700;
  letter-spacing: 0;
}

.filing-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.chart-unit {
  color: #6ab0d8;
  font-size: 0.58vw;
  line-height: 1;
  padding-left: 0.35vw;
  margin-bottom: 0.12vh;
}

.filing-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.24vh 0.46vw;
  padding: 0 0.35vw;
  margin-bottom: 0.08vh;
}

.filing-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.22vw;
  color: #cceeff;
  font-size: 0.52vw;
  line-height: 1;
  white-space: nowrap;
}

.filing-legend-dot {
  width: 0.38vw;
  height: 0.38vw;
  border-radius: 50%;
  box-shadow: 0 0 0.42vw currentColor;
}

.filing-chart-box {
  flex: 1;
  min-height: 0;
  margin-top: 0.08vh;
}

.column-panel ::v-deep(.tab-header) {
  align-items: center;
  height: 3.44vh;
  gap: 0.46vw;
  padding-top: 0;
  padding-left: 3.14vw;
  padding-right: 0.24vw;
  padding-bottom: 0;
  margin-bottom: 0.28vh;
}

.column-panel ::v-deep(.tab-text) {
  height: 100%;
  color: #d7f2ff;
  font-size: 0.82vw;
  font-weight: 700;
  letter-spacing: 0.02vw;
}

.column-panel ::v-deep(.tab-text.active) {
  color: #ffffff;
}

.tab-btn {
  min-width: clamp(56px, 3.34vw, 96px);
  height: 2.32vh;
  padding: 0 0.68vw;
  font-size: 0.6vw;
}

.stat-inline {
  display: flex;
  justify-content: space-between;
  gap: 0.42vw;
  padding: 0 0.18vw 0.18vh;
}

.stat-pair {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 0.22vh;
  min-width: 0;
}

.stat-pair em {
  font-style: normal;
  color: #9ad5f5;
  line-height: 1.2;
  font-size: 0.58vw;
}

.stat-pair strong {
  font-size: 1.08vw;
  font-weight: 700;
  line-height: 1;
}

.stat-inline .stat-pair:nth-child(1) strong {
  color: #19dcff;
}

.stat-inline .stat-pair:nth-child(2) strong {
  color: #ffffff;
}

.stat-inline .stat-pair:nth-child(3) strong {
  color: #82f34d;
}

.chart-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4vw;
  padding: 0 0.18vw 0.14vh;
}

.chart-axis-unit {
  color: #77b5d5;
  font-size: 0.54vw;
  line-height: 1;
  white-space: nowrap;
}

.chart-legend {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.38vw 0.58vw;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.22vw;
  color: #ccecff;
  font-size: 0.54vw;
  line-height: 1;
}

.legend-swatch {
  width: 0.62vw;
  height: 1.08vh;
  border-radius: 0.14vw;
  box-shadow: 0 0 0.42vw rgba(255, 255, 255, 0.16);
}

.chart-box {
  width: 100%;
}

.chart-box.short {
  flex: 1;
  min-height: 0;
  margin-top: 0.06vh;
}

.people-card {
  overflow: visible;
}

.people-card ::v-deep(.tab-header) {
  align-items: flex-start;
  gap: 0.34vw;
  padding-top: 0.18vh;
  padding-bottom: 0.18vh;
}

.people-card ::v-deep(.tab-extra) {
  align-items: flex-start;
}

.people-card ::v-deep(.people-tab-group) {
  display: inline-flex;
  width: auto;
}

.people-card .people-tab-btn {
  min-width: clamp(68px, 4.7vw, 104px);
  width: auto;
  padding: 0 0.46vw;
  font-size: 0.56vw;
  letter-spacing: 0;
}

.person-table {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: visible;
  color: #d7efff;
  font-size: 0.64vw;
  padding: 0.12vh 0.06vw 0;
}

.person-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.36vh;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.08vw;
}

.person-body::-webkit-scrollbar {
  width: 0.22vw;
}

.person-body::-webkit-scrollbar-track {
  background: rgba(14, 48, 104, 0.42);
  border-radius: 999vw;
}

.person-body::-webkit-scrollbar-thumb {
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(55, 214, 255, 0.92), rgba(16, 138, 255, 0.86));
  box-shadow: 0 0 0.4vw rgba(50, 195, 255, 0.22);
}

.person-header,
.person-row {
  display: grid;
  grid-template-columns: var(--person-grid-template);
  gap: 0.24vw;
  align-items: center;
  padding: 0 0.32vw;
}

.person-header {
  min-height: 2.56vh;
  color: rgba(191, 223, 246, 0.84);
  font-size: 0.74vw;
  font-weight: 600;
  letter-spacing: 0.02vw;
}

.person-row {
  position: relative;
  flex: 0 0 auto;
  min-height: 4vh;
  padding: 0.18vh 0.34vw;
  border: 0.05vw solid rgba(74, 193, 255, 0.18);
  border-radius: 0.18vw;
  background:
    linear-gradient(180deg, rgba(8, 34, 78, 0.46), rgba(4, 20, 49, 0.24)),
    linear-gradient(90deg, rgba(18, 92, 163, 0.08), rgba(18, 92, 163, 0));
  box-shadow: inset 0 0 0.82vw rgba(19, 126, 215, 0.06);
}

.person-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 4.2vh;
  border: 0.05vw solid rgba(74, 193, 255, 0.18);
  border-radius: 0.18vw;
  color: rgba(208, 237, 255, 0.72);
  font-size: 0.72vw;
  background:
    linear-gradient(180deg, rgba(8, 34, 78, 0.4), rgba(4, 20, 49, 0.2)),
    linear-gradient(90deg, rgba(18, 92, 163, 0.06), rgba(18, 92, 163, 0));
}

.person-row::before {
  content: '';
  position: absolute;
  left: 0.34vw;
  top: 0.62vh;
  bottom: 0.62vh;
  width: 0.09vw;
  border-radius: 999vw;
  background: linear-gradient(180deg, rgba(39, 227, 255, 1), rgba(39, 227, 255, 0.62));
  box-shadow: 0 0 0.52vw rgba(39, 227, 255, 0.28);
}

.person-header-cell,
.person-cell {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 0;
  height: 100%;
}

.person-header-cell {
  justify-content: center;
}

.person-cell {
  color: #f0fbff;
  font-size: 0.8vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.person-header-cell--left,
.person-cell--left {
  justify-content: flex-start;
}

.person-header-cell--center,
.person-cell--center {
  justify-content: center;
}

.person-cell--left {
  padding-left: 0.94vw;
}

.person-cell--primary {
  color: #22dcff;
  font-weight: 700;
  font-size: 1.18vw;
  letter-spacing: 0;
  text-shadow: 0 0 0.7vw rgba(34, 220, 255, 0.18);
}

.person-cell--tooltip {
  cursor: default;
  overflow: visible;
}

.person-cell--tooltip:hover {
  z-index: 6;
}

.person-cell--tooltip:hover::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: calc(100% + 0.06vh);
  width: 0.58vw;
  height: 0.58vw;
  border-right: 0.05vw solid rgba(132, 228, 255, 0.72);
  border-bottom: 0.05vw solid rgba(132, 228, 255, 0.72);
  background: linear-gradient(180deg, rgba(24, 71, 142, 0.98), rgba(12, 47, 114, 0.96));
  transform: translateX(-50%) rotate(45deg);
  box-shadow: 0.12vw 0.12vw 0.48vw rgba(22, 103, 196, 0.12);
}

.person-cell--tooltip:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 0.34vh);
  min-width: max-content;
  max-width: 12vw;
  padding: 0.56vh 0.84vw;
  border: 0.05vw solid rgba(132, 228, 255, 0.72);
  border-radius: 0.24vw;
  color: #dcefff;
  font-size: 0.76vw;
  line-height: 1.18;
  letter-spacing: 0;
  white-space: nowrap;
  background:
    linear-gradient(180deg, rgba(24, 71, 142, 0.98), rgba(12, 47, 114, 0.96)),
    rgba(13, 46, 106, 0.98);
  box-shadow:
    inset 0 0 0.72vw rgba(101, 220, 255, 0.08),
    0 0 1vw rgba(23, 111, 214, 0.18);
  pointer-events: none;
  transform: translateX(-50%);
}

.person-table--field .person-header {
  min-height: 2.56vh;
}

.person-table--field .person-cell {
  font-size: 0.68vw;
}

.person-table--field .person-cell--left {
  padding-left: 0.12vw;
}

.person-table--field .person-header-cell--left {
  justify-content: flex-start;
  padding-left: 0.12vw;
}

.person-table--field .person-cell--center {
  font-size: 0.66vw;
}

.person-table--visa .person-header {
  min-height: 2.72vh;
}

.person-table--visa .person-cell {
  font-size: 0.82vw;
}

.person-table--visa .person-cell--left {
  padding-left: 0.18vw;
}

.person-table--visa .person-cell--center:first-child {
  padding-left: 0.36vw;
}

.grain-card ::v-deep(.tab-header) {
  height: 3.42vh;
  padding-left: 3.16vw;
  padding-right: 0.16vw;
  margin-bottom: 0.14vh;
}

.grain-card ::v-deep(.tab-text) {
  font-size: 0.82vw;
  font-weight: 700;
  letter-spacing: 0;
}

.grain-info {
  display: flex;
  align-items: flex-start;
  gap: 0.56vw;
  color: #d7efff;
  font-size: 0.54vw;
  flex: 1;
  min-height: 0;
  padding: 0.44vh 0.18vw 0 0.08vw;
}

.grain-info--no-gauge {
  justify-content: flex-start;
}

.grain-info--no-gauge .grain-copy {
  flex: 1 1 auto;
}

.grain-copy {
  display: flex;
  flex: 0 0 37.5%;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1.26vh;
  min-width: 0;
  padding-top: 1.3vh;
  padding-left: 0.14vw;
  transform: translateY(0.42vh);
}

.grain-copy--transfer {
  flex-basis: 100%;
  padding-top: 0.66vh;
  padding-right: 0.24vw;
  transform: none;
}

.grain-copy--residue,
.grain-copy--processing {
  flex: 0 0 43.5%;
  gap: 0.82vh;
  padding-top: 0.78vh;
  transform: none;
}

.grain-label {
  color: #f4fbff;
  font-size: 1.02vw;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.01vw;
  text-shadow: 0 0 0.76vw rgba(255, 255, 255, 0.18);
}

.grain-metric-list {
  display: flex;
  align-items: flex-start;
  gap: 1.54vw;
}

.grain-metric-list--transfer {
  justify-content: space-between;
  gap: 0.88vw;
  padding-right: 0.28vw;
}

.grain-metric-list--residue,
.grain-metric-list--processing {
  flex-direction: column;
  gap: 1.16vh;
  padding-top: 0.18vh;
}

.grain-metric {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0.24vh;
  min-width: 0;
}

.grain-metric--transfer {
  min-width: 0;
}

.grain-metric-inline {
  display: inline-flex;
  align-items: baseline;
  gap: 0.2vw;
  min-width: 0;
  white-space: nowrap;
}

.grain-metric-inline-label {
  color: #d8efff;
  font-size: 1vw;
  font-weight: 700;
  line-height: 1;
}

.grain-metric--residue,
.grain-metric--processing {
  gap: 0.3vh;
}

.grain-metric-value {
  display: inline-flex;
  align-items: baseline;
  gap: 0.08vw;
  color: #9ed3ec;
  line-height: 1;
  font-weight: 700;
}

.grain-metric-value strong {
  line-height: 0.9;
  color: #f4fbff;
  font-size: 1.96vw;
}

.grain-metric-value.is-primary strong {
  color: #f4fbff;
  font-size: 1.96vw;
}

.grain-metric-value.is-secondary strong {
  color: #f4fbff;
  font-size: 1.96vw;
}

.grain-metric-value span {
  padding-bottom: 0.1vh;
  font-size: 0.84vw;
  font-weight: 700;
  color: #f4fbff;
}

.grain-metric-label {
  color: #d5ecff;
  font-size: 0.68vw;
  line-height: 1.18;
}

.gauge-wrap {
  display: flex;
  flex: 0 0 63.5%;
  align-items: stretch;
  justify-content: center;
  min-width: 0;
  margin-right: -0.04vw;
  padding-top: 0;
  transform: translateY(-0.46vh);
}

.grain-info--transfer .grain-metric-value strong {
  font-size: 2vw;
}

.grain-info--transfer .grain-metric-value span {
  font-size: 0.78vw;
}

.grain-info--residue .grain-metric-value,
.grain-info--processing .grain-metric-value {
  gap: 0.12vw;
}

.grain-info--residue .grain-metric-value strong,
.grain-info--processing .grain-metric-value strong {
  font-size: 1.2vw;
}

.grain-info--residue .grain-metric-value span,
.grain-info--processing .grain-metric-value span {
  font-size: 0.96vw;
}

.grain-info--residue .grain-metric-label,
.grain-info--processing .grain-metric-label {
  font-size: 0.74vw;
}

.grain-info--residue .gauge-wrap,
.grain-info--processing .gauge-wrap {
  flex-basis: 56.5%;
  margin-right: 0;
  transform: translateY(-0.18vh);
}

.gauge-canvas {
  display: block;
  align-self: stretch;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 12.2vh;
}

.grain-tab-group {
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.grain-card .grain-tab-btn {
  min-width: 4.34vw;
  height: 2.14vh;
  padding: 0 0.54vw;
  border-radius: 0.42vw;
  font-size: 0.58vw;
}

.grain-card .grain-tab-btn.active {
  color: #f8fdff;
  background:
    linear-gradient(180deg, rgba(31, 174, 255, 0.28), rgba(13, 99, 226, 0.12)),
    linear-gradient(180deg, rgba(25, 158, 255, 0.96), rgba(14, 97, 226, 0.78));
  box-shadow:
    inset 0 0 0 0.05vw rgba(129, 242, 255, 0.52),
    0 0 0.9vw rgba(31, 174, 255, 0.24);
}

.audit-card ::v-deep(.tab-header) {
  height: 3.12vh;
  padding-left: 3.18vw;
  padding-right: 0.02vw;
  margin-bottom: 0.18vh;
}

.audit-card ::v-deep(.tab-text) {
  font-size: 0.8vw;
  font-weight: 700;
  letter-spacing: 0;
}

.audit-card .audit-tab-btn {
  min-width: 4.44vw;
  height: 2.08vh;
  border-radius: 0.42vw;
  font-size: 0.62vw;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 0.64vh;
  padding-top: 0.06vh;
  flex: 1;
  min-height: 0;
}

.progress-header,
.progress-row {
  display: grid;
  grid-template-columns: minmax(112px, 0.94fr) minmax(0, 2.12fr) minmax(96px, 0.9fr);
  align-items: center;
  gap: 0.62vw;
}

.progress-header {
  color: rgba(143, 215, 255, 0.88);
  font-size: 0.5vw;
  padding: 0 0.04vw;
}

.progress-head-label {
  line-height: 1;
}

.progress-head-label--bar {
  text-align: center;
}

.progress-head-label--value {
  text-align: center;
}

.progress-row {
  color: #d9f3ff;
  font-size: 0.58vw;
}

.progress-label {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.42vw;
  width: 100%;
  min-height: 2.28vh;
  padding: 0 0.42vw 0 0.22vw;
  border: 0.05vw solid rgba(67, 197, 255, 0.18);
  border-radius: 0.08vw;
  background:
    linear-gradient(90deg, rgba(12, 123, 122, 0.54), rgba(7, 79, 120, 0.32)),
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0 0.34vw, rgba(255, 255, 255, 0) 0.34vw 0.68vw);
}

.progress-label::before {
  content: '';
  position: absolute;
  left: 0.08vw;
  top: 0.3vh;
  bottom: 0.3vh;
  width: 0.14vw;
  border-radius: 999vw;
  background: rgba(217, 254, 255, 0.95);
  box-shadow: 0 0 0.38vw rgba(217, 254, 255, 0.48);
}

.progress-dot {
  display: inline-block;
  width: 0.16vw;
  height: 1.2vh;
  border-radius: 0.08vw;
  box-shadow: 0 0 0.36vw rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
}

.progress-text {
  color: #39f0cb;
  white-space: nowrap;
  font-size: 0.68vw;
  font-weight: 700;
}

.progress-bar {
  display: flex;
  align-items: center;
  height: 0.62vh;
  border-radius: 999vw;
  overflow: hidden;
  background: linear-gradient(90deg, rgba(9, 94, 134, 0.72), rgba(11, 78, 127, 0.26));
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  box-shadow: 0 0 0.7vw rgba(11, 247, 216, 0.26);
}

.progress-value {
  display: inline-flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  height: 2.28vh;
  border: 0.05vw solid rgba(97, 194, 255, 0.18);
  border-radius: 0.08vw;
  color: #f2fbff;
  font-size: 0.72vw;
  font-weight: 500;
  background:
    repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0 0.34vw, rgba(255, 255, 255, 0.01) 0.34vw 0.68vw),
    linear-gradient(180deg, rgba(16, 53, 104, 0.72), rgba(8, 30, 68, 0.48));
}
</style>
